import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutGrid, Hammer, Calendar, Banknote, Menu as MenuIcon,
  Users, Car, FileText, Package, Building2, UsersRound, ShieldCheck,
  LogOut, Wrench, X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { can, ROUTE_PAGE } from "@/lib/permissions";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { VerifyBanner } from "@/components/VerifyBanner";
import { ReadOnlyBanner } from "@/components/ReadOnlyBanner";

const WORKSHOP = [
  { label: "Dashboard", icon: LayoutGrid, path: "/dashboard", testId: "sidebar-dashboard", page: "dashboard" },
  { label: "Quotes & Estimates", icon: FileText, path: "/quotations", testId: "sidebar-quotations", page: "quotes" },
  { label: "Jobs", icon: Hammer, path: "/jobs", testId: "sidebar-jobs", page: "jobs" },
  { label: "Bookings & Calendar", icon: Calendar, path: "/calendar", testId: "sidebar-calendar", page: "bookings" },
  { label: "Finance", icon: Banknote, path: "/money", testId: "sidebar-finance", page: "finance" },
  { label: "Account & Settings", icon: MenuIcon, path: "/more", testId: "sidebar-account", page: "settings" },
];
const MANAGEMENT = [
  { label: "Customers", icon: Users, path: "/customers", testId: "sidebar-customers", page: "customers" },
  { label: "Vehicles", icon: Car, path: "/vehicles", testId: "sidebar-vehicles", page: "vehicles" },
  { label: "Parts & Inventory", icon: Package, path: "/parts", testId: "sidebar-parts", page: "parts" },
  { label: "Suppliers & Purchasing", icon: Building2, path: "/suppliers", testId: "sidebar-suppliers", page: "suppliers" },
  { label: "Team Management", icon: UsersRound, path: "/team", testId: "sidebar-team", page: "team" },
  { label: "MOT & Vehicle Checks", icon: ShieldCheck, path: "/mot-lookup", testId: "sidebar-mot", page: "mot" },
];

function NavItem({ item, onNavigate, badge = 0 }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      data-testid={item.testId}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-4 py-2.5 mx-2 text-sm font-medium transition-colors ${
          isActive
            ? "bg-brand/15 text-white"
            : "text-white/70 hover:bg-white/5 hover:text-white"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`h-[18px] w-[18px] ${isActive ? "text-brand" : ""}`} />
          <span className="flex-1">{item.label}</span>
          {badge > 0 && (
            <span data-testid={`${item.testId}-badge`} className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#25D366] px-1.5 text-[11px] font-bold text-white">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function SidebarInner({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col bg-midnight">
      <div className="flex items-center gap-3 border-b border-white/10 px-5 py-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10">
          <Wrench className="h-5 w-5 text-brand" />
        </div>
        <div>
          <p className="font-display text-[15px] font-bold tracking-widest text-white">PRO GARAGES</p>
          <p className="text-[11px] text-white/60">Garage Management System</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-3">
        <p className="px-5 pb-2 pt-2 text-[10px] font-extrabold tracking-[0.16em] text-white/40">WORKSHOP</p>
        {WORKSHOP.filter((i) => can(user, i.page)).map((i) => <NavItem key={i.path} item={i} onNavigate={onNavigate} />)}
        <p className="px-5 pb-2 pt-4 text-[10px] font-extrabold tracking-[0.16em] text-white/40">MANAGEMENT</p>
        {MANAGEMENT.filter((i) => can(user, i.page)).map((i) => <NavItem key={i.path} item={i} onNavigate={onNavigate} />)}
      </div>
      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 rounded-lg bg-white/5 p-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand text-sm font-extrabold text-white">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-bold text-white">{user?.name}</p>
            <p className="text-[10px] font-bold tracking-wide text-white/50">{(user?.role || "").toUpperCase()}</p>
          </div>
          <button
            data-testid="sidebar-logout"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="text-white/60 hover:text-white"
          >
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function AppLayout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const page = ROUTE_PAGE[location.pathname];
  const blocked = page && !can(user, page);
  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-[260px] shrink-0 sticky top-0 h-screen">
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-midnight px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10">
              <Wrench className="h-4 w-4 text-brand" />
            </div>
            <span className="font-display text-sm font-bold tracking-widest text-white">PRO GARAGES</span>
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="rounded-md p-1.5 text-white">
                {open ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] p-0 border-0">
              <SidebarInner onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </header>

        <main key={location.pathname} className="flex-1 animate-fade-in-up">
          <VerifyBanner />
          <ReadOnlyBanner />
          <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-8 sm:py-8">
            {blocked ? (
              <div className="mx-auto mt-10 max-w-md rounded-xl border border-border bg-card p-8 text-center" data-testid="no-access">
                <ShieldCheck className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-4 font-display text-lg font-bold text-midnight">No access</p>
                <p className="mt-1 text-sm text-muted-foreground">You don&apos;t have permission to view this page. Contact your garage owner if you need access.</p>
              </div>
            ) : children}
          </div>
        </main>
      </div>
    </div>
  );
}
